import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AppWrap } from '../../wrapper';
import { urlFor, client } from '../../client';
import './Skills.scss';

const fadeUp = {
	hidden: { y: 40, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const gridStagger = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.12, delayChildren: 0.08 } },
};

const cardReveal = {
	hidden: { y: 60, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const MARQUEE_COPIES = 4;

// Years arrive as free text ("2024", "2021 — 2023"); rank on the first 4-digit
// run so the newest lands at index 0 and carries the "Current" marker.
const parseYear = (year) => {
	const match = String(year ?? '').match(/\d{4}/);
	return match ? Number(match[0]) : -Infinity;
};

const Skills = () => {
	const [experiences, setExperiences] = useState([]);
	const [skills, setSkills] = useState([]);

	useEffect(() => {
		let mounted = true;

		client.fetch('*[_type == "experiences"]').then((data) => {
			if (!mounted) return;
			const sorted = [...(data || [])].sort(
				(a, b) =>
					parseYear(b.year) - parseYear(a.year) ||
					String(b.year).localeCompare(String(a.year), undefined, { numeric: true })
			);
			setExperiences(sorted);
		});

		client.fetch('*[_type == "skills"]').then((data) => {
			if (mounted) setSkills(data || []);
		});

		return () => {
			mounted = false;
		};
	}, []);

	// two marquee rows drifting in opposite directions; one row when the list is short
	const marqueeRows = useMemo(() => {
		if (skills.length === 0) return [];
		if (skills.length < 8) return [skills];
		const mid = Math.ceil(skills.length / 2);
		return [skills.slice(0, mid), skills.slice(mid)];
	}, [skills]);

	return (
		<div className='skills-content'>
			<motion.div
				className='skills-head'
				variants={fadeUp}
				initial='hidden'
				whileInView='visible'
				viewport={{ once: true, amount: 0.4 }}
			>
				<div>
					<p className='eyebrow'>Capabilities</p>
					<h2 className='skills-heading'>
						My <span className='text-gradient'>skills</span>
						<br />
						& <em>experience</em>
					</h2>
				</div>
				<p className='skills-intro'>
					The stack behind every build — and the roles where it was sharpened. Hover
					the belt to pause it.
				</p>
			</motion.div>

			{marqueeRows.length > 0 && (
				<motion.div
					className='skills-stack-row'
					variants={fadeUp}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.3 }}
				>
					<h3 className='skills-subhead'>
						Tech Stack <span>· {skills.length}</span>
					</h3>

					{marqueeRows.map((row, rowIndex) => (
						<div
							className={`skills-marquee ${rowIndex % 2 ? 'reverse' : ''}`}
							key={rowIndex}
						>
							<div
								className='marquee-track'
								style={{ '--marquee-duration': `${Math.max(row.length * 4, 18)}s` }}
							>
								{Array.from({ length: MARQUEE_COPIES }).map((_, copy) => (
									<div
										className='marquee-group'
										key={copy}
										aria-hidden={copy > 0 || undefined}
									>
										{row.map((item) => (
											<div className='skill-chip' key={`${item.name}-${copy}`}>
												<span className='skill-chip-icon'>
													<img src={urlFor(item.icon)} alt='' />
												</span>
												<span className='skill-chip-name'>{item.name}</span>
											</div>
										))}
									</div>
								))}
							</div>
						</div>
					))}
				</motion.div>
			)}

			{experiences.length > 0 && (
				<div className='skills-exp'>
					<motion.h3
						className='skills-subhead'
						variants={fadeUp}
						initial='hidden'
						whileInView='visible'
						viewport={{ once: true, amount: 0.4 }}
					>
						Experience
					</motion.h3>

					<div className='exp-timeline'>
						<motion.span
							className='exp-rail'
							aria-hidden='true'
							initial={{ scaleY: 0 }}
							whileInView={{ scaleY: 1 }}
							viewport={{ once: true, amount: 0.1 }}
							transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
						/>

						<motion.ol
							className='exp-items'
							variants={gridStagger}
							initial='hidden'
							whileInView='visible'
							viewport={{ once: true, amount: 0.15 }}
						>
							{experiences.map((experience, index) => (
								<motion.li
									className={`exp-item${index === 0 ? ' is-current' : ''}`}
									variants={cardReveal}
									key={`${experience.year}-${index}`}
								>
									<div className='exp-time'>
										<span className='exp-year'>{experience.year}</span>
									</div>

									<span className='exp-node' aria-hidden='true' />

									<article className='exp-card'>
										<div className='exp-time'>
											{index === 0 && <span className='exp-badge'>Current</span>}
										</div>
										<div className='exp-roles'>
											{(experience.works || []).map((work) => (
												<div className='exp-role' key={work.name}>
													<h4>{work.name}</h4>
													{work.company && (
														<span className='exp-company'>@ {work.company}</span>
													)}
													{work.desc && <p>{work.desc}</p>}
												</div>
											))}
										</div>
									</article>
								</motion.li>
							))}
						</motion.ol>
					</div>
				</div>
			)}
		</div>
	);
};

export default AppWrap(Skills, 'skills');
